import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner } from '@patternfly/react-core';
import { ExclamationTriangleIcon, CalendarAltIcon, UsersIcon } from '@patternfly/react-icons';
import toast from 'react-hot-toast';
import { GradientButton } from '@app/components/GradientButton';
import { TopUpModal } from '@app/Subscription/TopUpModal';
import { colors, radii, PURPLE_GRADIENT } from '@app/components/designTokens';
import {
    bookRun,
    bookRunsAuto,
    generateIdempotencyKey,
    getAvailableSlots,
    getSubscription,
    SlotOption
} from '@app/utils/utils';
import {
    ActivationError,
    ActivationErrorBody,
    SubscriptionResponse,
    SubscriptionTier
} from '@app/Subscription/types/subscription';

type Mode = 'pick' | 'auto';

interface ScheduleRunsStepProps {
    campaignId: number;
    /** Called after a successful booking session — parent can refresh + close modals. */
    onBooked?: () => void;
    /** Called when the user cancels out of a block-error view. */
    onCloseModal?: () => void;
    /** When true, do not navigate to /campaigns/{id} after booking (e.g. when reused in a modal on the detail page). */
    suppressNavigate?: boolean;
}

const ONE_HOUR_MS = 60 * 60 * 1000;

const ScheduleRunsStep: React.FC<ScheduleRunsStepProps> = ({
    campaignId,
    onBooked,
    onCloseModal,
    suppressNavigate
}) => {
    const navigate = useNavigate();

    const [mode, setMode] = useState<Mode>('pick');

    const [subscription, setSubscription] = useState<SubscriptionResponse | null>(null);
    const [loadingSub, setLoadingSub] = useState(true);

    // --- Mode A: slot picker ---
    const [slots, setSlots] = useState<SlotOption[]>([]);
    const [loadingSlots, setLoadingSlots] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [slotsError, setSlotsError] = useState<string | null>(null);
    const [endReached, setEndReached] = useState(false);
    const [selected, setSelected] = useState<Set<string>>(new Set());

    // --- Mode B: auto-book ---
    const [autoCount, setAutoCount] = useState<number>(1);

    // --- Booking state ---
    const [booking, setBooking] = useState(false);
    const [blockError, setBlockError] = useState<ActivationErrorBody | null>(null);
    const [genericError, setGenericError] = useState<string | null>(null);
    const [showTopUp, setShowTopUp] = useState(false);

    const remaining = subscription
        ? Math.max(0, subscription.credits.limit - subscription.credits.used) + (subscription.credits.purchased ?? 0)
        : 0;

    // Belt-and-braces: drop slots within the server's minimum lead time. The server enforces 24h for
    // owners (1h for admins); a 1h floor here is a safe lower bound that never hides a valid slot.
    const visibleSlots = useMemo(() => {
        const cutoff = Date.now() + ONE_HOUR_MS;
        return slots.filter((s) => new Date(s.slotStart).getTime() > cutoff);
    }, [slots]);

    useEffect(() => {
        let cancelled = false;
        setLoadingSub(true);
        getSubscription()
            .then((sub) => {
                if (!cancelled) setSubscription(sub);
            })
            .catch(() => {
                if (!cancelled) setSubscription(null);
            })
            .finally(() => {
                if (!cancelled) setLoadingSub(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        setLoadingSlots(true);
        setSlotsError(null);
        getAvailableSlots(campaignId, { limit: 50 })
            .then((rows) => {
                if (cancelled) return;
                setSlots(rows);
                setEndReached(rows.length < 50);
            })
            .catch((err: any) => {
                if (cancelled) return;
                setSlotsError(err?.message || 'Failed to load available slots.');
            })
            .finally(() => {
                if (!cancelled) setLoadingSlots(false);
            });
        return () => {
            cancelled = true;
        };
    }, [campaignId]);

    const handleLoadMore = async () => {
        if (loadingMore || endReached || slots.length === 0) return;
        setLoadingMore(true);
        try {
            const last = slots[slots.length - 1];
            const more = await getAvailableSlots(campaignId, { limit: 50, after: last.slotStart });
            // De-dupe by slotStart in case the cursor returns an inclusive boundary.
            const seen = new Set(slots.map((s) => s.slotStart));
            const fresh = more.filter((s) => !seen.has(s.slotStart));
            setSlots((prev) => prev.concat(fresh));
            setEndReached(more.length < 50);
        } catch (err: any) {
            setSlotsError(err?.message || 'Failed to load more slots.');
        } finally {
            setLoadingMore(false);
        }
    };

    const toggleSlot = (slotStart: string) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(slotStart)) next.delete(slotStart);
            else next.add(slotStart);
            return next;
        });
    };

    const formatLocalTime = (iso: string): string => {
        try {
            const d = new Date(iso);
            return d.toLocaleString(undefined, {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                timeZoneName: 'short'
            });
        } catch {
            return iso;
        }
    };

    const goToPlan = () => {
        if (onCloseModal) onCloseModal();
        navigate('/plan');
    };

    const handlePostBookingNavigate = () => {
        if (onBooked) onBooked();
        if (!suppressNavigate) {
            navigate(`/campaigns/${campaignId}`);
        }
    };

    // --- Mode A: serial per-slot booking ---
    const handleBookSelected = async () => {
        if (selected.size === 0) return;
        if (selected.size > remaining) {
            toast.error(`You only have ${remaining} credit${remaining === 1 ? '' : 's'} remaining.`);
            return;
        }

        setBooking(true);
        setBlockError(null);
        setGenericError(null);

        const slotsToBook = Array.from(selected);
        let booked = 0;
        let skipped = 0;
        const bookedStarts: string[] = [];

        for (let i = 0; i < slotsToBook.length; i++) {
            const slotStart = slotsToBook[i];
            const progressToast = toast.loading(
                `Booking slot ${i + 1} of ${slotsToBook.length}…`
            );
            try {
                await bookRun(campaignId, {
                    slotStart,
                    idempotencyKey: generateIdempotencyKey()
                });
                booked++;
                bookedStarts.push(slotStart);
                toast.success(`Booked slot ${i + 1} of ${slotsToBook.length}`, { id: progressToast });
            } catch (err: any) {
                toast.dismiss(progressToast);
                if (err instanceof ActivationError) {
                    // Quota / no-sub: stop the loop and surface the block UI.
                    setBlockError(err.body);
                    setBooking(false);
                    return;
                }
                if (err?.status === 409) {
                    skipped++;
                    toast(`Slot at ${formatLocalTime(slotStart)} was just taken — skipped.`, { icon: '⚠' });
                    continue;
                }
                // Unknown error: surface and stop.
                setGenericError(err?.message || 'Failed to book one or more slots.');
                setBooking(false);
                return;
            }
        }

        // Remove successfully-booked slots from local list + selection.
        const bookedSet = new Set(bookedStarts);
        setSlots((prev) => prev.filter((s) => !bookedSet.has(s.slotStart)));
        setSelected(new Set());

        if (booked > 0) {
            toast.success(
                `Booked ${booked} of ${slotsToBook.length} slots${skipped ? ` (${skipped} skipped)` : ''}.`
            );
            setBooking(false);
            handlePostBookingNavigate();
            return;
        }

        toast.error(`No slots booked${skipped ? ` (${skipped} already taken).` : '.'}`);
        setBooking(false);
    };

    // --- Mode B: auto-book N ---
    const handleAutoBook = async () => {
        if (autoCount <= 0) return;
        setBooking(true);
        setBlockError(null);
        setGenericError(null);

        const t = toast.loading(`Booking ${autoCount} run${autoCount === 1 ? '' : 's'}…`);
        try {
            const result = await bookRunsAuto(campaignId, {
                count: autoCount,
                idempotencyKey: generateIdempotencyKey()
            });
            toast.success(`Booked ${result.booked} of ${result.requested} requested.`, { id: t });
            setBooking(false);
            handlePostBookingNavigate();
        } catch (err: any) {
            toast.dismiss(t);
            if (err instanceof ActivationError) {
                setBlockError(err.body);
            } else {
                setGenericError(err?.message || 'Failed to auto-book runs.');
            }
            setBooking(false);
        }
    };

    // --- Styles ---
    const toggleBtn = (active: boolean): React.CSSProperties => ({
        padding: '8px 16px',
        border: 'none',
        background: active ? '#ffffff' : 'transparent',
        color: active ? colors.title : colors.subtle,
        fontWeight: 600,
        fontSize: '13px',
        borderRadius: radii.button - 2,
        cursor: 'pointer',
        boxShadow: active ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
        transition: 'all 0.15s ease'
    });

    const styles: Record<string, React.CSSProperties> = {
        container: { padding: '0' },
        toggleRow: {
            display: 'inline-flex',
            background: '#f3f4f6',
            padding: '4px',
            borderRadius: radii.button,
            marginBottom: '20px'
        },
        infoCard: {
            background: '#f5f3ff',
            border: `1px solid #ddd6fe`,
            borderRadius: radii.card,
            padding: '16px 20px',
            marginBottom: '20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '16px'
        },
        infoBig: {
            fontSize: '22px',
            fontWeight: 700,
            color: colors.title,
            letterSpacing: '-0.5px'
        },
        infoLabel: {
            fontSize: '12px',
            color: colors.subtle,
            marginTop: '2px'
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse' as any,
            background: '#ffffff',
            border: `1px solid ${colors.border}`,
            borderRadius: radii.input,
            overflow: 'hidden'
        },
        th: {
            padding: '10px 12px',
            textAlign: 'left' as any,
            fontSize: '11px',
            fontWeight: 700,
            color: colors.subtle,
            textTransform: 'uppercase' as any,
            letterSpacing: '0.5px',
            background: '#f9fafb',
            borderBottom: `1px solid ${colors.border}`
        },
        td: {
            padding: '12px',
            fontSize: '14px',
            color: colors.body,
            borderBottom: `1px solid ${colors.border}`
        },
        rowSelected: { background: '#f5f3ff' },
        tableWrap: {
            maxHeight: '420px',
            overflowY: 'auto' as any,
            border: `1px solid ${colors.border}`,
            borderRadius: radii.input
        },
        footerStrip: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '16px',
            padding: '12px 16px',
            background: '#f9fafb',
            border: `1px solid ${colors.border}`,
            borderRadius: radii.input,
            fontSize: '13px',
            color: colors.muted
        },
        errorCard: {
            background: '#fef2f2',
            border: `1px solid #fecaca`,
            borderRadius: radii.input,
            padding: '12px 14px',
            marginBottom: '14px',
            color: colors.danger,
            fontSize: '13px'
        },
        blockCard: {
            background: '#fffbeb',
            border: `1px solid #fde68a`,
            borderRadius: radii.card,
            padding: '20px',
            marginBottom: '16px'
        },
        emptyState: {
            padding: '40px 20px',
            textAlign: 'center' as any,
            color: colors.subtle,
            fontSize: '14px'
        },
        loadMoreBtn: {
            display: 'block',
            margin: '12px auto 0',
            padding: '8px 16px',
            background: '#ffffff',
            border: `1px solid ${colors.border}`,
            borderRadius: radii.button,
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: 600,
            color: colors.body
        },
        numberInput: {
            width: '100%',
            padding: '12px 14px',
            fontSize: '16px',
            border: `2px solid ${colors.border}`,
            borderRadius: radii.input,
            outline: 'none',
            boxSizing: 'border-box' as any
        },
        smallNote: {
            color: colors.placeholder,
            fontSize: '12px',
            marginTop: '12px',
            textAlign: 'center' as any
        }
    };

    // ---- Render: block errors take priority ----
    if (blockError) {
        const reason = blockError.reason;
        if (reason === 'no_active_subscription') {
            return (
                <div style={styles.container}>
                    <div style={styles.blockCard}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                            <ExclamationTriangleIcon style={{ color: colors.warning, fontSize: '22px' }} />
                            <strong style={{ color: '#92400e' }}>Subscription required</strong>
                        </div>
                        <p style={{ margin: 0, color: '#92400e', fontSize: '14px', lineHeight: 1.6 }}>
                            Subscribe to a plan, or buy campaign credits individually to book runs.
                        </p>
                    </div>
                    <GradientButton onClick={() => setShowTopUp(true)} isBlock>
                        Buy campaign credits
                    </GradientButton>
                    <GradientButton appearance="secondary" onClick={goToPlan} isBlock style={{ marginTop: '8px' }}>
                        Choose a plan
                    </GradientButton>
                    <TopUpModal
                        isOpen={showTopUp}
                        context="campaign"
                        onClose={() => setShowTopUp(false)}
                        onPurchased={(sub) => {
                            if (sub) setSubscription(sub);
                            setBlockError(null);
                        }}
                    />
                </div>
            );
        }
        if (reason === 'tier_limit') {
            const r = blockError.remaining ?? 0;
            const upgradeTo: SubscriptionTier | undefined = blockError.upgradeTo;
            return (
                <div style={styles.container}>
                    <div style={styles.blockCard}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                            <ExclamationTriangleIcon style={{ color: colors.warning, fontSize: '22px' }} />
                            <strong style={{ color: '#92400e' }}>Out of credits</strong>
                        </div>
                        <p style={{ margin: 0, color: '#92400e', fontSize: '14px', lineHeight: 1.6 }}>
                            You have <strong>{r}</strong> credit{r === 1 ? '' : 's'} remaining this period.
                            Buy extra credits{upgradeTo && <>, or upgrade to <strong>{upgradeTo}</strong> for more every month</>}.
                        </p>
                    </div>
                    <GradientButton onClick={() => setShowTopUp(true)} isBlock>
                        Buy campaign credits
                    </GradientButton>
                    <GradientButton appearance="secondary" onClick={goToPlan} isBlock style={{ marginTop: '8px' }}>
                        {upgradeTo ? `Upgrade to ${upgradeTo}` : 'View plans'}
                    </GradientButton>
                    <TopUpModal
                        isOpen={showTopUp}
                        context="campaign"
                        onClose={() => setShowTopUp(false)}
                        onPurchased={(sub) => {
                            if (sub) setSubscription(sub);
                            setBlockError(null);
                        }}
                    />
                </div>
            );
        }
        return (
            <div style={styles.container}>
                <div style={styles.errorCard}>
                    {blockError.message || "This action can't be completed right now."}
                </div>
                <GradientButton onClick={goToPlan} isBlock>
                    View plans
                </GradientButton>
            </div>
        );
    }

    if (loadingSub) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '32px', gap: '12px', color: colors.subtle }}>
                <Spinner size="lg" />
                <span>Checking your subscription…</span>
            </div>
        );
    }

    const selectedCount = selected.size;
    const remainingAfter = Math.max(0, remaining - selectedCount);

    const renderPickMode = () => (
        <>
            <div style={styles.infoCard}>
                <div>
                    <div style={styles.infoBig}>{remaining}</div>
                    <div style={styles.infoLabel}>credits remaining this period</div>
                </div>
                <CalendarAltIcon style={{ fontSize: '28px', color: colors.accent }} />
            </div>

            {slotsError && <div style={styles.errorCard}>{slotsError}</div>}

            {loadingSlots ? (
                <div style={{ padding: '32px', textAlign: 'center', color: colors.subtle }}>
                    <Spinner size="md" />
                    <div style={{ marginTop: 8 }}>Loading available slots…</div>
                </div>
            ) : visibleSlots.length === 0 ? (
                <div style={styles.emptyState}>
                    No slots available for this campaign right now.
                </div>
            ) : (
                <>
                    <div style={styles.tableWrap}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={{ ...styles.th, width: 40 }}></th>
                                    <th style={styles.th}>Slot start (30 min)</th>
                                    <th style={{ ...styles.th, textAlign: 'right' }}>Eligible drivers</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleSlots.map((s) => {
                                    const isSel = selected.has(s.slotStart);
                                    return (
                                        <tr
                                            key={s.slotStart}
                                            onClick={() => toggleSlot(s.slotStart)}
                                            style={{
                                                ...(isSel ? styles.rowSelected : {}),
                                                cursor: 'pointer'
                                            }}
                                        >
                                            <td style={styles.td}>
                                                <input
                                                    type="checkbox"
                                                    checked={isSel}
                                                    onChange={() => toggleSlot(s.slotStart)}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            </td>
                                            <td style={styles.td}>{formatLocalTime(s.slotStart)}</td>
                                            <td style={{ ...styles.td, textAlign: 'right' }}>
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: colors.muted }}>
                                                    <UsersIcon style={{ fontSize: '12px' }} />
                                                    {s.eligibleDriverCount}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {!endReached && (
                        <button
                            type="button"
                            style={styles.loadMoreBtn}
                            onClick={handleLoadMore}
                            disabled={loadingMore}
                        >
                            {loadingMore ? 'Loading…' : 'Load more'}
                        </button>
                    )}
                </>
            )}

            <div style={styles.footerStrip}>
                <span>
                    Selected <strong style={{ color: colors.title }}>{selectedCount}</strong>
                    {' · '}
                    Cost <strong style={{ color: colors.title }}>{selectedCount} credit{selectedCount === 1 ? '' : 's'}</strong>
                    {' · '}
                    Remaining after <strong style={{ color: remainingAfter === 0 && selectedCount > 0 ? colors.danger : colors.title }}>{remainingAfter}</strong>
                </span>
            </div>

            {genericError && <div style={{ ...styles.errorCard, marginTop: 14 }}>{genericError}</div>}

            <div style={{ marginTop: 16 }}>
                <GradientButton
                    onClick={handleBookSelected}
                    isDisabled={booking || selectedCount === 0 || selectedCount > remaining}
                    isLoading={booking}
                    isBlock
                >
                    {booking
                        ? 'Booking…'
                        : selectedCount === 0
                            ? 'Select at least one slot'
                            : `Book ${selectedCount} run${selectedCount === 1 ? '' : 's'} · ${selectedCount} credit${selectedCount === 1 ? '' : 's'}`}
                </GradientButton>
            </div>

            <p style={styles.smallNote}>
                One credit is charged per booked slot. Each slot is a 30-minute run offered to a single eligible driver.
            </p>
        </>
    );

    const renderAutoMode = () => (
        <>
            <div style={styles.infoCard}>
                <div>
                    <div style={styles.infoBig}>{remaining}</div>
                    <div style={styles.infoLabel}>credits remaining this period</div>
                </div>
                <CalendarAltIcon style={{ fontSize: '28px', color: colors.accent }} />
            </div>

            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: colors.body, marginBottom: 8 }}>
                How many runs?
            </label>
            <input
                type="number"
                min={1}
                max={Math.max(1, remaining)}
                value={autoCount}
                onChange={(e) => {
                    const n = parseInt(e.target.value, 10);
                    if (isNaN(n)) {
                        setAutoCount(1);
                    } else {
                        setAutoCount(Math.max(1, Math.min(Math.max(1, remaining), n)));
                    }
                }}
                style={styles.numberInput}
                disabled={remaining === 0}
            />
            <p style={{ ...styles.smallNote, textAlign: 'left', marginTop: 8 }}>
                The server picks the <strong>{autoCount}</strong> soonest eligible 30-minute slot{autoCount === 1 ? '' : 's'} and assigns a driver to each.
            </p>

            {genericError && <div style={{ ...styles.errorCard, marginTop: 14 }}>{genericError}</div>}

            <div style={{ marginTop: 16 }}>
                <GradientButton
                    onClick={handleAutoBook}
                    isDisabled={booking || autoCount <= 0 || autoCount > remaining}
                    isLoading={booking}
                    isBlock
                >
                    {booking
                        ? 'Booking…'
                        : `Book ${autoCount} soonest run${autoCount === 1 ? '' : 's'} · ${autoCount} credit${autoCount === 1 ? '' : 's'}`}
                </GradientButton>
            </div>

            {remaining === 0 && (
                <p style={{ ...styles.smallNote, color: colors.danger, marginTop: 12 }}>
                    You have no credits remaining this period.
                </p>
            )}
        </>
    );

    return (
        <div style={styles.container}>
            <div style={styles.toggleRow}>
                <button
                    type="button"
                    style={toggleBtn(mode === 'pick')}
                    onClick={() => setMode('pick')}
                >
                    Pick slots
                </button>
                <button
                    type="button"
                    style={toggleBtn(mode === 'auto')}
                    onClick={() => setMode('auto')}
                >
                    Auto-book N soonest
                </button>
            </div>

            {mode === 'pick' ? renderPickMode() : renderAutoMode()}
        </div>
    );
};

export { ScheduleRunsStep };
